import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add request interceptor to include auth token and version
api.interceptors.request.use(async (config) => {
    // Add auth token if available
    const token = localStorage.getItem('access_token');
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Ensure localStorage has a version (failsafe)
    let savedVersion = localStorage.getItem('app_version');
    if (!savedVersion) {
        savedVersion = 'v2';
        localStorage.setItem('app_version', 'v2');
    }
    
    // Only add header for non-version endpoints
    if (!config.url.includes('/version/')) {
        config.headers['X-App-Version'] = savedVersion;
    }
    
    return config;
}, (error) => {
    return Promise.reject(error);
});

// Response interceptor for handling 401 errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        // If 401 Unauthorized, redirect to login
        if (error.response?.status === 401) {
            localStorage.removeItem('access_token');
            // Only redirect if not already on login or portal
            if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/portal')) {
                window.location.href = '/login';
            }
        }
        console.error('API Error:', {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            data: error.response?.data,
        });
        return Promise.reject(error);
    }
);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard');

// Products
export const getProducts = (search = '') => api.get(`/products${search ? `?search=${search}` : ''}`);
export const getProduct = (id) => api.get(`/products/${id}`);
export const getProductByBarcode = (barcode) => api.get(`/products/barcode/${barcode}`);
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);
export const getCategories = () => api.get('/categories');

// Stock
export const getStockItems = (pallet = '', productId = '') => {
    const params = new URLSearchParams();
    if (pallet) params.append('pallet', pallet);
    if (productId) params.append('product_id', productId);
    return api.get(`/stock?${params.toString()}`);
};
export const addToStock = (data) => api.post('/stock', data);
export const updateStockItem = (id, quantity, palletLocation) => 
    api.put(`/stock/${id}?quantity=${quantity}&pallet_location=${encodeURIComponent(palletLocation)}`);
export const deleteStockItem = (id) => api.delete(`/stock/${id}`);
export const moveToStore = (stockItemId, quantity) => 
    api.post('/stock/move-to-store', { stock_item_id: stockItemId, quantity });

// Store
export const getStoreItems = () => api.get('/store');
export const updateStoreItem = (id, quantity) => api.put(`/store/${id}?quantity=${quantity}`);
export const deleteStoreItem = (id) => api.delete(`/store/${id}`);
export const reserveFromStore = (data) => api.post('/store/reserve', data);

// Reservations
export const getReservations = (customerId = '', status = '') => {
    const params = new URLSearchParams();
    if (customerId) params.append('customer_id', customerId);
    if (status) params.append('status', status);
    return api.get(`/reservations?${params.toString()}`);
};
export const updateReservationStatus = (id, status) => 
    api.put(`/reservations/${id}/status?status=${status}`);
export const deleteReservation = (id) => api.delete(`/reservations/${id}`);

// Customers
export const getCustomers = (search = '', safeMode = false) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (safeMode) params.append('safe_mode', 'true');
    const queryString = params.toString();
    return api.get(`/customers${queryString ? `?${queryString}` : ''}`);
};
export const getCustomer = (customerId) => api.get(`/customers/${customerId}`);
export const createCustomer = (data) => api.post('/customers', data);
export const updateCustomer = (customerId, data) => api.put(`/customers/${customerId}`, data);
export const deleteCustomer = (customerId) => api.delete(`/customers/${customerId}`);

// Search
export const searchProducts = (query) => api.get(`/search?q=${encodeURIComponent(query)}`);

export default api;
// Build: 20260312_142358
